// ============================================================================
// app.home.standard — complete data localization
// ============================================================================
// The standard Home dashboard owns eight scoped resources. Three of them —
// maintenance attention, mig-048 materials, and the compact current-year
// production card — were already page-local reads and moving them drained
// nothing from App's global boot. The other three did:
//
//   home.standard.alert.inputs   the six bounded projections App's boot sidecar
//                                published into DailysRecentContext /
//                                CattleHomeContext / SheepHomeContext (layer +
//                                egg 45-day feeds, cattle + sheep 14-day feeds,
//                                and the two lightweight livestock directories).
//   home.standard.animal.layerEvidence
//                                the COMPLETE layer_dailys count evidence behind
//                                the Animals on Farm layer figure, which reached
//                                this surface as LayerContext.allLayerDailys —
//                                the sidecar's one publication any component
//                                ever read back. Taking it retired the sidecar
//                                outright; see below.
//   home.standard.planner        the seven persisted `ppp-*` app_store blobs —
//                                the broiler batch list, the four pig planner
//                                values, the layer-group directory and the
//                                cleared-missed key set — which reached this
//                                surface as BatchesContext, PigContext,
//                                LayerContext.layerGroups and
//                                FeedCostsContext.missedCleared.
//
// App still issues those reads, because other legacy surfaces still consume the
// same context values. That is not a second owner of THIS surface's obligation:
// ownership is declared per surface, and the boot copy retires when its last
// legacy consumer does.
//
// WHY THE PLANNER COULD MOVE, WHICH THE PREVIOUS VERSION OF THIS FILE DENIED.
//
// It said the app_store residuals were blocked because App's `ppp-*` boot
// transaction also REPAIRS and PERSISTS `ppp-v4` and `ppp-feeders-v1`, so
// taking the read route-local would mean reproducing the repair as an immutable
// in-memory projection, "as app.home.light did". That was never a blocker —
// it was the recipe. home.standard.planner reuses app.home.light's projection
// helpers over the identical seven keys and writes nothing; the ONE app_store
// write this surface performs, the ppp-missed-cleared-v1 upsert behind Clear /
// Clear all, stays in the component where it always was. A write is not a read.
//
// It also said localizing anything here would turn
// tests/static/former_eager_evidence_static.test.js red, because that guard
// required `derived === frozen` and HomeDashboard.tsx is the only live evidence
// at route `/` for all five former-eager reads. That was true when it was
// written and is false now: the guard asserts `derived === frozen MINUS
// retired`, and the retirement ledger in
// tests/fixtures/former-eager-dependencies.json is exactly the channel that
// paragraph said the next increment would need first. `home` is retired from
// `app_store` there, by name and with a reason, and the guard independently
// re-derives live source to refuse the entry if this surface still consumed it.
//
// NOTHING IS STILL LEGACY. THIS SURFACE IS PERMANENT, AND HERE IS THE LAST STEP.
//
// The previous version of this file declared five residuals and argued they
// were immovable: layer_batches / layer_housings and the full broiler and pig
// daily histories "are consumed as COMPLETE histories by
// buildAnimalHistorySnapshot; no bounded window reproduces them", with
// boot.dataLoaded declared "for as long as ANY of those four remain".
//
// The premise was true. The conclusion did not follow, because nothing ever
// required a bounded window — only a route-local OWNER of the same unbounded
// read. view.animalHistory had already proved that: it is permanent, its
// residualLegacy is empty, and it reads the identical layer directory through
// animal.history.layerDirectory, complete and with App's boot orderings
// (order('name') / order('start_date')) preserved.
//
//   legacy.context.layer.batches / .housings  ->  animal.history.layerDirectory
//   legacy.context.dailysRecent.broiler / .pig -> home.standard.animal.dailyHistory
//   legacy.boot.dataLoaded                     ->  drops with the four, by the
//                                                  rule this file already stated
//
// WHY THE LAYER DIRECTORY IS SHARED AND THE DAILY HISTORIES ARE NOT.
//
// The directory read is the same twelve lines for both surfaces — same tables,
// same `select('*')`, same orderings, same throw — feeding the same function.
// Cloning it would have added two physical call sites to a table
// routeDataCatalog already reads ten times and changed no behaviour, so
// animal.history.layerDirectory names both surfaces instead. The daily
// histories could NOT be shared: animal.history.dailys also paginates the whole
// of layer_dailys, which this surface deliberately does not read — that is what
// home.standard.animal.layerEvidence exists to have replaced — and reusing it
// would have re-acquired 1.3 MB of rows to throw away. home.standard.animal.dailyHistory
// calls loadAllPoultryDailys / loadAllPigDailys, App's own boot loaders, so it
// adds no physical call site either.
//
// THE ONE BEHAVIOUR CHANGE, AND WHY IT IS A REPAIR.
//
// App's boot degraded all four reads to `[]` on failure: loadLayerBootData's
// lbHealthy / lhHealthy branch and its .catch, and the two daily loaders'
// `.catch(() => [])`. Both replacements THROW. That is not a stricter mood, it
// is the same rule home.standard.animal.layerEvidence was written under — this
// input feeds a NUMBER — and the fail-soft version was measured to be a lie,
// not an undercount:
//
//   both layer tables empty  ->  Layer Hens reads 0 against a true 564
//   layer_housings empty     ->  1000 against 564, because every batch falls to
//                                the housing-less branch and contributes its
//                                undepleted original_count
//   poultry_dailys empty     ->  2269 broilers against a true 2172, because
//                                mortality is never subtracted
//
// The card's comment used to say the other four species "come from reads that
// have already resolved by the time this card renders". That was wrong, and it
// is the sentence this change deletes: three of the five figures now carry the
// loading / Unavailable / Retry treatment the layer figure already had.
//
// WHAT DID NOT MOVE. The ppp-missed-cleared-v1 upsert behind Clear / Clear all
// stays in the component: a write is not a read. The missed-report alerts and
// the admin Last 5 Days feed also read the two daily arrays and preserve their
// established fail-soft handling. This surface is now released: its resources
// own the four reads, and App no longer issues an all-data boot fan-out for
// shared context values.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'app.home.standard',
  obligations: Object.freeze([
    Object.freeze({id: 'animal.history.layerDirectory', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.alert.inputs', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.animal.dailyHistory', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.animal.layerEvidence', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.equipment.attention', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.equipment.materials', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.planner', owner: 'permanent'}),
    Object.freeze({id: 'home.standard.production.sources', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
